import mixpanel from 'mixpanel-browser';
import React, { useContext, useState, createContext, useReducer, useEffect } from 'react';
import { useHistory } from 'react-router';
import { API } from '../api';
import { ApiError, ApiStatus, isApiError } from '../api/api';
import { FileUploadResult, ImageProvider, UserAssetType, EntrepreneurUpdateBody, EntrepreneurProfileBody, ConnectorReferralData, ProfileAccessLevel, ErrorCode } from '../types/types';
import { useUser } from './UserContext';
import { EntrepreneurActionType, entrepreneurReducer, initialState } from './EntrepreneurReducer';
import { getProfileAction } from './EntrepreneurActions';
import { useAlert } from './AlertContext';

interface EntrepreneurContextProps {
  getProfileImageProvider: (id: number) => ImageProvider,
  getLogoImageProvider: (id: number) => ImageProvider,
  updateProfile: (data: EntrepreneurUpdateBody) => Promise<boolean>,
  setIsSelectingCompanyLogo?: (isSelecting: boolean) => void
  isEditable: boolean,
  referral?: string,
  referralOrigin?: string,
  // values
  profile?: EntrepreneurProfileBody
  connectorReferralData?: ConnectorReferralData
  // errors
  getProfileApiError?: ApiError
  // api status
  getProfileApiStatus?: ApiStatus
}

export const EntrepreneurContext = createContext<EntrepreneurContextProps>({
  getProfileImageProvider: () => ({ imgUrl: '', editable: false, isSelectingImage: false }),
  getLogoImageProvider: () => ({ imgUrl: '', editable: false, isSelectingImage: false }),
  updateProfile: async () => (false),
  isEditable: false
});

export const useEntContext = () => useContext(EntrepreneurContext);

interface EntrepreneurContextProviderProps {
  children: any
  accessLevel: ProfileAccessLevel
  isConnectorUser: boolean
  entProfileId: number
  referral?: string
  referralOrigin?: string
  permalinkId: string | undefined
}

export const EntrepreneurContextProvider: React.FC<EntrepreneurContextProviderProps> = ({ children, accessLevel, referral, referralOrigin, entProfileId, permalinkId }) => {
  const { saveProfileImage, getAccessToken, userInfo, presentLogin } = useUser();
  const [tempImgUrl, setTempImgUrl] = useState<string | null>(null);
  const [tempLogoUrl, setTempLogoUrl] = useState<string | null>(null);
  const [state, dispatch] = useReducer(entrepreneurReducer, initialState);
  const [isSelectingCompanyLogo, setIsSelectingCompanyLogo] = useState<boolean>(false);
  const [wasUnpublished, setWasUnpublished] = useState<boolean>(false);
  const { postNewAlert } = useAlert();
  const history = useHistory();

  const {
    profile,
    connectorReferralData,
    getProfileApiError,
    getProfileApiStatus
  } = state;

  const isEditable = profile ? profile.editable : false;

  useEffect(() => {
    if (profile != null && !profile.isPublished) {
      setWasUnpublished(true);
    }
    if (profile != null && profile.isPublished && wasUnpublished) {
      setWasUnpublished(false);
      mixpanel.track('Entrepreneur published', { EntrepreneurId: profile.id });
    }
  }, [profile, wasUnpublished]);

  const updateProfile = async (values: EntrepreneurUpdateBody) => {
    try {
      const id = userInfo?.profileId;
      if (id) {
        const accessToken = await getAccessToken();
        await API.updateEntrepreneurProfile(accessToken, values);
        getProfileAction(dispatch, { profileId: id, token: accessToken });
      } else {
        throw new Error('profile id is missing, can not update profile');
      }
      return true;
    } catch (e: unknown) {
      console.error(e);
      if (e instanceof Object && isApiError(e)) {
        const apiError: ApiError = e as ApiError;
        if (apiError.code === '4015') {
          // error 4015 indicate that profile is not complete and list all the fields
          const m = JSON.parse(apiError.message);
          console.log('publish validation error', m);
        }
        postNewAlert(apiError.message);
      } else {
        postNewAlert('An unexpected error occurred updating the profile.');
      }
      return false;
    }
  };

  const updateProfileImage = async (image: FileUploadResult) => {
    setTempImgUrl(image.url);
    try {
      const newProfileUrl = await saveProfileImage(image);
      if (newProfileUrl) {
        dispatch({
          type: EntrepreneurActionType.GetProfileSuccess,
          payload: { profile: { ...profile, profileImageUrl: newProfileUrl } }
        });
        if (!profile?.isPublished) {
          // profile may have been published as side effect of successful upload
          getProfileAction(dispatch, { profileId: entProfileId, token: await getAccessToken(), referralId: referral, originId: referralOrigin });
        }
      }
    } catch (e: unknown) {
      console.error('error updating profile picture: ', e);
      postNewAlert('An error occurred updating the profile picture.');
    } finally {
      setTempImgUrl(null);
    }
  };

  const updateLogoImage = async (image: FileUploadResult) => {
    setTempLogoUrl(image.url);
    try {
      const token = await getAccessToken();
      const { location } = await API.uploadImageAsset(token, {
        image: image.data,
        mime: image.mime,
        type: UserAssetType.Logo
      });

      let newLogoUrl: string | undefined;
      if (location) {
        newLogoUrl = location;
      }

      if (newLogoUrl) {
        dispatch({
          type: EntrepreneurActionType.GetProfileSuccess,
          payload: { profile: { ...profile, logo: newLogoUrl } }
        });
        if (!profile?.isPublished) {
          // profile may have been published as side effect of successful upload
          getProfileAction(dispatch, { profileId: entProfileId, token: await getAccessToken(), referralId: referral, originId: referralOrigin });
        }
      }
    } catch (e: unknown) {
      console.error('error updating profile picture: ', e);
      postNewAlert('An error occurred updating the logo.');
    } finally {
      setTempLogoUrl(null);
    }
  };

  const getProfileImageProvider = (id: number) => {
    const imgUrl = profile?.profileImageUrl;
    if (isEditable) {
      return {
        imgUrl: imgUrl || '',
        updateImage: updateProfileImage,
        tempImgUrl,
        editable: true
      };
    }
    return { imgUrl: imgUrl || '', editable: false };
  };

  const getLogoImageProvider = (id: number) => {
    const imgUrl = profile?.logo;
    if (isEditable) {
      return {
        imgUrl: imgUrl || '',
        updateImage: updateLogoImage,
        tempImgUrl: tempLogoUrl,
        editable: true,
        isSelectingImage: isSelectingCompanyLogo,
        setIsSelectingImage: setIsSelectingCompanyLogo
      };
    }
    return { imgUrl: imgUrl || '', editable: false };
  };

  useEffect(() => {
    const getProfile = async () => {
      if (getProfileApiStatus === undefined || entProfileId !== profile?.id) {
        if (userInfo != null) {
          getProfileAction(dispatch, { profileId: entProfileId, token: await getAccessToken(), referralId: referral, originId: referralOrigin, permalinkId });
        } else if (accessLevel === ProfileAccessLevel.Anonymous) {
          getProfileAction(dispatch, { profileId: entProfileId, referralId: referral, originId: referralOrigin, permalinkId });
        }
      }
    };
    getProfile();
  }, [entProfileId, referral, getProfileApiStatus, getAccessToken, accessLevel, userInfo, referralOrigin, permalinkId, profile?.id]);

  useEffect(() => {
    if (getProfileApiError?.code === ErrorCode.ReferralNotFound || getProfileApiError?.code === ErrorCode.UnexpectedProfileId) {
      // user has tried passing a referral id for a non-existent referral or one with a different ent profile than was requested
      // gently escort to login
      presentLogin();
    } else if (getProfileApiError != null) {
      postNewAlert(`Profile fetch failed. Detail: ${getProfileApiError?.message}`);
      if (getProfileApiError.code === ErrorCode.NoReadAccess) {
        history.push('/');
        dispatch({ type: EntrepreneurActionType.ClearError });
      }
    }
    // history dependency not needed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getProfileApiError, presentLogin, postNewAlert]);

  return (
    <EntrepreneurContext.Provider value={{
      getProfileImageProvider,
      getLogoImageProvider,
      updateProfile,
      setIsSelectingCompanyLogo,
      referral,
      referralOrigin,
      isEditable,
      profile,
      connectorReferralData,
      getProfileApiError,
      getProfileApiStatus
    }}
    >
      {children}
    </EntrepreneurContext.Provider>
  );
};

